/* eslint-disable react-hooks/exhaustive-deps, jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  Button,
  Divider,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  Zoom,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useUser } from "../../hooks/Authorize";
import LockIcon from "@mui/icons-material/Lock";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";

const OfferPayment = () => {
  const stripe = useStripe();
  const elements = useElements();
  const platformUser = useUser();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get("planId");
  const orgId = searchParams.get("orgId");
  const navigate = useNavigate();
  console.log(platformUser)
  const [plan, setPlan] = useState(null);
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [cardholderName, setCardholderName] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [billingAddress, setFormData] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
  });
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!planId) {
        navigate("/myStore");
        return;
      }

      try {
        const planRes = await axios.get(`/store/plan/${planId}`);
        if (planRes.data.success) {
          const planData = planRes.data.data;
          setPlan(planData);

          try {
            const offerRes = await axios.get(`/store/plan/offer-by-plan/${planId}`);
            if (offerRes.data.success) {
              setOffer(offerRes.data.data);
            }
          } catch (offerErr) {
            console.warn("No offer found for this plan, showing plan details only.");
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setSnackbar({ open: true, message: "Failed to load plan details.", severity: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [planId]);

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !plan) return;

    if (!cardholderName) {
      setSnackbar({ open: true, message: "Please enter the cardholder name.", severity: "warning" });
      return;
    }

    setProcessing(true);
    const cardElement = elements.getElement(CardNumberElement);

    try {
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: cardholderName,
          email: platformUser?.recoveryEmail || platformUser?.email,
          address: billingAddress,
        },
      });

      if (pmError) throw pmError;

      const { token, error: tokenError } = await stripe.createToken(cardElement);
      if (tokenError) throw tokenError;

      const payload = {
        userId: platformUser?._id,
        name: cardholderName,
        email: platformUser?.recoveryEmail || platformUser?.email,
        paymentMethod: paymentMethod.id,
        tokenId: token.id,
        card: token.card,
        billing_details: {
          address: billingAddress,
          name: cardholderName,
          email: platformUser?.recoveryEmail || platformUser?.email,
        },
        plan: plan.name,
        planId: plan._id,
        amount: plan.amount,
        orgId: orgId,
        billingCycle: plan.interval === "year" ? "Yearly" : plan.interval === "month" ? "Monthly" : "One-time",
      };

      const response = await axios.post(
        `/store/subscription/checkout`,
        payload
      );

      if (response.status === 200) {
        localStorage.setItem("subscriptionId", response.data.subscriptionId);
        setShowSuccessPopup(true);

        let timer = 3;
        const intervalId = setInterval(() => {
          timer -= 1;
          setCountdown(timer);
          if (timer <= 0) {
            clearInterval(intervalId);
            const type = plan?.type?.toLowerCase();
            let redirectUrl = "https://app.clikkle.com/";

            const subdomains = {
              hr: "https://hr.clikkle.com/login",
              projects: "https://projects.clikkle.com/login",
              chat: "https://chat.clikkle.com/login",
              campaigns: "https://campaigns.clikkle.com/login",
              worksuite: "https://app.clikkle.com/login",
              files: "https://files.clikkle.com/login",
              esign: "https://esign.clikkle.com/login",
            };

            if (subdomains[type]) {
              redirectUrl = subdomains[type];
            }
            window.location.replace(redirectUrl);
          }
        }, 1000);
      }
    } catch (error) {
      console.error("Payment error:", error);
      setSnackbar({ open: true, message: error.response?.data?.message || error.message || "Payment failed.", severity: "error" });
    } finally {
      setProcessing(false);
    }
  };

  const handleBack = () => {
    if (offer?.slug) {
      window.location.href = `https://clikkle.com/offers/${offer.slug}`;
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" bgcolor="#fff">
        <CircularProgress />
      </Box>
    );
  }

  const cardElementOptions = {
    style: {
      base: {
        fontSize: "16px",
        color: "#1a1f36",
        "::placeholder": { color: "#aab7c4" },
        fontFamily: "Inter, system-ui, sans-serif",
      },
      invalid: { color: "#9e2146" },
    },
  };

  return (
    <Box sx={{
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      minHeight: "100vh",
      bgcolor: "#fff",
      color: "#1a1f36"
    }}>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
      </Snackbar>

      {/* Left Side: Product Info */}
      <Box sx={{
        width: { xs: "100%", md: "45%" },
        p: { xs: 4, sm: 6, md: 8 },
        borderRight: { md: "1px solid #e3e8ee" },
        borderBottom: { xs: "1px solid #e3e8ee", md: "none" },
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f7f9fc"
      }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ alignSelf: "flex-start", mb: { xs: 4, md: 6 }, color: "#697386", textTransform: "none", "&:hover": { bgcolor: "transparent", color: "#1a1f36" } }}
        >
          Back
        </Button>

        <Box sx={{ mb: 4, display: "flex", alignItems: "center" }}>
          <img
            alt="Clikkle Technologies logo"
            style={{ height: "40px", width: "auto" }}
            src={
              offer?.logos?.light ||
              "https://cdn.clikkle.com/images/worksuite/2025/Clikkle%20Worksuite%20Blue%20(%20Black%20Text).png"
            }
          />
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" sx={{ color: "#697386", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.025em", mb: 1, fontSize: { xs: "0.7rem", md: "0.875rem" } }}>
            Subscribe to {plan?.type?.toUpperCase()}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: "#1a1f36", fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" } }}>
            {offer?.name || plan?.name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", mb: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#1a1f36", fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
              ${plan?.amount}
            </Typography>
            <Typography variant="subtitle1" sx={{ color: "#697386", ml: 1, fontSize: { xs: "0.9rem", md: "1rem" } }}>
              {plan?.interval === "one-time" ? "one-time" : `per ${plan?.interval}`}
            </Typography>
          </Box>
        </Box>

        {offer?.hero?.subtitle && (
          <Typography variant="body1" sx={{ color: "#4f566b", mb: 4, lineHeight: 1.6, fontSize: { xs: "0.9rem", md: "1rem" } }}>
            {offer.hero.subtitle}
          </Typography>
        )}

        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: "#1a1f36" }}>Included in this plan:</Typography>
          <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
            {plan?.permission?.slice(0, 5).map((perm, i) => (
              <Box key={i} component="li" sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <CheckIcon sx={{ color: "#3c4257", fontSize: 18, mr: 1.5 }} />
                <Typography variant="body2" sx={{ color: "#4f566b", fontSize: { xs: "0.8rem", md: "0.875rem" } }}>{perm.content}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ mt: { xs: 4, md: "auto" }, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>

          <Typography variant="caption" sx={{ color: "#697386", textDecoration: "none" }} component="a" href="https://clikkle.com/terms-of-service" target="_blank" rel="noopener noreferrer">
            Terms
          </Typography>

          <Divider orientation="vertical" flexItem sx={{ height: 12, my: "auto", display: { xs: "none", sm: "block" } }} />

          <Typography variant="caption" sx={{ color: "#697386", textDecoration: "none" }} component="a" href="https://clikkle.com/privacy-policy" target="_blank" rel="noopener noreferrer">
            Privacy
          </Typography>
        </Box>
      </Box>

      {/* Right Side: Payment Fields */}
      <Box sx={{
        width: { xs: "100%", md: "55%" },
        p: { xs: 4, sm: 6, md: 8 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
      }}>
        <Box component="form" onSubmit={handlePayment} sx={{ maxWidth: 480, width: "100%", mx: "auto" }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, color: "#1a1f36", fontSize: { xs: "1.25rem", md: "1.5rem" } }}>
            Payment details
          </Typography>

          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, color: "#4f566b" }}>Contact information</Typography>
            <TextField
              fullWidth
              variant="outlined"
              label="Email"
              value={platformUser?.recoveryEmail || platformUser?.email || ""}
              disabled
              size="small"
              InputProps={{
                startAdornment: <LockIcon sx={{ mr: 1, fontSize: 18, color: "#697386" }} />,
              }}
              sx={{
                // 1. Fix the Label color
                "& .MuiInputLabel-root.Mui-disabled": {
                  color: "#697386", // Or whatever color you prefer for the label
                },
                // 2. Fix the Input text color
                "& .MuiOutlinedInput-root.Mui-disabled": {
                  "& .MuiOutlinedInput-input": {
                    color: "#1a1f36",
                    WebkitTextFillColor: "#1a1f36", // Fixes visibility in Safari/Chrome
                  },
                  // 3. Keep the border visible
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e3e8ee !important",
                  },
                },
                bgcolor: "#f7f9fc",
              }}
            />
            <Typography variant="caption" sx={{ color: "#697386", mt: 0, display: "block" }}>
              Confirmations will be sent to your recovery email.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#4f566b" }}>Card information</Typography>
            <Box sx={{ border: "1px solid #e3e8ee", borderRadius: "4px", p: "12px 16px", mb: 2 }}>
              <CardNumberElement options={cardElementOptions} />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ flex: 1, border: "1px solid #e3e8ee", borderRadius: "4px", p: "12px 16px" }}>
                <CardExpiryElement options={cardElementOptions} />
              </Box>
              <Box sx={{ flex: 1, border: "1px solid #e3e8ee", borderRadius: "4px", p: "12px 16px" }}>
                <CardCvcElement options={cardElementOptions} />
              </Box>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#4f566b" }}>Cardholder name</Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Name on card"
              value={cardholderName}
              onChange={(e) => setCardholderName(e.target.value)}
              size="small"
              sx={{
                "& .MuiOutlinedInput-input": { color: "#1a1f36" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e3e8ee" }
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#4f566b" }}>Billing address</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  name="line1"
                  placeholder="Address line 1"
                  value={billingAddress.line1}
                  onChange={handleBillingChange}
                  sx={{
                    "& .MuiOutlinedInput-input": { color: "#1a1f36" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e3e8ee" }
                  }} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  name="line2"
                  placeholder="Address line 2"
                  value={billingAddress.line2}
                  onChange={handleBillingChange}
                  sx={{
                    "& .MuiOutlinedInput-input": { color: "#1a1f36" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e3e8ee" }
                  }} />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  name="city"
                  placeholder="City"
                  value={billingAddress.city}
                  onChange={handleBillingChange}
                  sx={{
                    "& .MuiOutlinedInput-input": { color: "#1a1f36" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e3e8ee" }
                  }} />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  name="postal_code"
                  placeholder="Postal code"
                  value={billingAddress.postal_code}
                  onChange={handleBillingChange}
                  sx={{
                    "& .MuiOutlinedInput-input": { color: "#1a1f36" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e3e8ee" }
                  }} />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#4f566b" }}>Promotion code</Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter coupon code"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              size="small"
              sx={{
                "& .MuiOutlinedInput-input": { 
                  color: "#1a1f36",
                  textTransform: "uppercase"
                },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e3e8ee" }
              }}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={processing || !stripe}
            sx={{
              bgcolor: "#0048e5",
              color: "#fff",
              py: 1.5,
              minHeight: "48px", // Ensure consistent height
              borderRadius: "4px",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "16px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              "&:hover": { bgcolor: "#0039b3" },
              "&.Mui-disabled": {
                bgcolor: processing ? "#0048e5" : "#e3e8ee", // Keep color if processing
                color: processing ? "#fff" : "rgba(0, 0, 0, 0.26)",
                opacity: processing ? 0.8 : 1
              }
            }}
          >
            {processing ? <CircularProgress size={24} sx={{ color: "inherit" }} /> : `Subscribe`}
          </Button>

          <Typography variant="caption" sx={{ display: "block", textAlign: "center", mt: 3, color: "#697386" }}>
            By clicking Subscribe, you agree to Clikkle's <a href="https://clikkle.com/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: "#0048e5", textDecoration: "none" }}>terms and conditions</a>.
          </Typography>
        </Box>
      </Box>

      {/* Success Popup - Always Light Theme */}
      <Dialog
        open={showSuccessPopup}
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 2,
            textAlign: "center",
            maxWidth: "400px",
            bgcolor: "#fff", // Force white background
            color: "#1a1f36", // Force dark text
          }
        }}
      >
        <DialogContent sx={{ bgcolor: "#fff" }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "#e6f4ea",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1
              }}
            >
              <CheckIcon sx={{ color: "#1e8e3e", fontSize: 35 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#1a1f36" }}>
              Welcome to Clikkle!
            </Typography>
            <Typography variant="body1" sx={{ color: "#4f566b" }}>
              Your payment was successful. You're being redirected to your app in <strong>{countdown}</strong> seconds.
            </Typography>
            <CircularProgress
              variant="determinate"
              value={(countdown / 3) * 100}
              sx={{ color: "#0048e5", mt: 1 }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default OfferPayment;
